import { Input, InputNumber, Select, Tooltip } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
	REWRITE_OP_KINDS,
	makeRewriteOp,
	readRewriteOp,
	writeRewriteOp,
} from 'src/_shared/services/job-sync/serde';

const OP_LABELS = { append: 'append', prepend: 'prepend', replaceSuffix: 'replace suffix', split: 'split', firstNonNull: 'first non-null' };

// Edits a field mapping's optional `rewrite` pipeline — an ordered list of string ops applied to the
// mapping's resolved value (e.g. rewrite an iCIMS view URL into the apply-prefill URL). When empty it
// collapses to a single subtle "rewrite" link so rows that don't rewrite stay clean. `onChange`
// receives the next RewriteOp[] (empty array clears it).
export default function RewriteOpsEditor({ ops, onChange }) {
	const setOp = (i, next) => onChange(ops.map((o, idx) => (idx === i ? next : o)));
	const removeOp = (i) => onChange(ops.filter((_, idx) => idx !== i));
	const addOp = () => onChange([...ops, makeRewriteOp('append')]);

	// Changing the op kind: carry the text between append/prepend; reset when moving to/from the
	// structured kinds (replaceSuffix, split, firstNonNull) — their fields don't translate.
	const changeKind = (i, op, nextKind) => {
		const cur = readRewriteOp(op);
		const structured = (k) => k === 'replaceSuffix' || k === 'split' || k === 'firstNonNull';
		if (structured(nextKind) || structured(cur.kind)) setOp(i, makeRewriteOp(nextKind));
		else setOp(i, writeRewriteOp(nextKind, { text: cur.text }));
	};

	if (!ops.length) {
		return (
			<button type="button" className="js-linkbtn js-rewrite-add" onClick={addOp}>
				<PlusOutlined /> rewrite
			</button>
		);
	}

	return (
		<div className="js-rewrite">
			<span className="js-rewrite-tag js-mono">rewrite</span>
			<div className="js-rewrite-ops">
				{ops.map((op, i) => {
					const { kind, text, find, replacement, sep, index } = readRewriteOp(op);
					return (
						/* eslint-disable-next-line react/no-array-index-key -- ops are a plain ordered list edited in place */
						<div className="js-rewrite-op" key={i}>
							<div className="js-token js-rewrite-kind">
								<Select
									value={kind}
									onChange={(k) => changeKind(i, op, k)}
									options={REWRITE_OP_KINDS.map((k) => ({ value: k, label: OP_LABELS[k] }))}
									dropdownMatchSelectWidth={false}
									style={{ width: '100%' }}
								/>
							</div>
							{kind === 'replaceSuffix' ? (
								<>
									<div className="js-token js-rewrite-text">
										<Input
											value={find}
											placeholder="find suffix"
											onChange={(e) => setOp(i, writeRewriteOp('replaceSuffix', { find: e.target.value, replacement }))}
										/>
									</div>
									<span className="js-rewrite-arrow" aria-hidden>
										→
									</span>
									<div className="js-token js-rewrite-text">
										<Input
											value={replacement}
											placeholder="replace with"
											onChange={(e) => setOp(i, writeRewriteOp('replaceSuffix', { find, replacement: e.target.value }))}
										/>
									</div>
								</>
							) : kind === 'split' ? (
								<>
									<div className="js-token js-rewrite-text">
										<Input
											value={sep}
											placeholder="separator"
											onChange={(e) => setOp(i, writeRewriteOp('split', { sep: e.target.value, index }))}
										/>
									</div>
									<span className="js-rewrite-arrow" aria-hidden>
										[
									</span>
									<div className="js-token js-rewrite-index">
										<InputNumber
											value={index}
											min={0}
											precision={0}
											placeholder="index"
											onChange={(v) => setOp(i, writeRewriteOp('split', { sep, index: v == null ? 0 : v }))}
											style={{ width: '100%' }}
										/>
									</div>
									<span className="js-rewrite-arrow" aria-hidden>
										]
									</span>
								</>
							) : kind === 'firstNonNull' ? (
								<span className="js-muted">array → first non-null element</span>
							) : (
								<div className="js-token js-rewrite-text">
									<Input
										value={text}
										placeholder={kind === 'append' ? 'text to append' : 'text to prepend'}
										onChange={(e) => setOp(i, writeRewriteOp(kind, { text: e.target.value }))}
									/>
								</div>
							)}
							<Tooltip title="Remove op">
								<button
									type="button"
									className="js-iconbtn js-danger"
									onClick={() => removeOp(i)}
									aria-label="Remove rewrite op"
								>
									<DeleteOutlined />
								</button>
							</Tooltip>
						</div>
					);
				})}
				<button type="button" className="js-linkbtn" onClick={addOp}>
					<PlusOutlined /> op
				</button>
			</div>
		</div>
	);
}
